import { FC, useMemo } from 'react';

import { BundlesLayout, EBundlesViewModel } from '@appcharge/shared-ui';
import { Offer } from 'common/contracts';
import { EBundlesInternalViewModel, ESaleDiscountDisplayType } from 'constants/enums';
import { usePriceForPreview } from 'views/Offers/hooks/usePriceForPreview';
import { renderingUtil } from 'utils/renderingUtil';
import { resolveProgressBarPoints } from 'views/Offers/utils/resolveProgressBarPoints';
import { normalizeBgColor } from 'views/OfferDesign/OfferDesign.utils';
import { OfferPreviewShell } from 'views/Offers/components/OfferPreviewShell';

import { OfferDesignData, PreviewProduct } from './BundlesForm.types';

interface IProgressBarFormValues {
  progressBarId: string | null;
  progressBarPoints: number | null;
}

interface BundlePreviewProps {
  getOfferDesign: { data?: OfferDesignData };
  productsListForPreview: PreviewProduct[];
  price: number;
  salePercentage: number;
  salePercentageDisplayType?: ESaleDiscountDisplayType;
  emblemBadges?: object[];
  ribbonBadges?: object[];
  progressBarFormValues?: IProgressBarFormValues;
  progressBarOffers?: Offer[];
}

export const BundlePreview: FC<BundlePreviewProps> = ({
  getOfferDesign,
  productsListForPreview,
  price,
  salePercentage,
  salePercentageDisplayType,
  emblemBadges,
  ribbonBadges,
  progressBarFormValues,
  progressBarOffers
}) => {
  const offerDesignData = getOfferDesign.data;
  const { priceDisplay, priceInUsdCents } = usePriceForPreview({ price });

  const productsList = useMemo(() => {
    if (!productsListForPreview.length) return [];

    const hasSale = (salePercentage ?? 0) > 0;
    if (!hasSale) return productsListForPreview;

    return productsListForPreview.map((product, index) => {
      if (index !== 0) return product;
      return {
        ...product,
        strokedSaleQuantity: String(
          product.quantity ?? product.quantityDisplay ?? ''
        )
      };
    });
  }, [productsListForPreview, salePercentage]);

  const progressBarPoints = useMemo(
    () =>
      resolveProgressBarPoints({
        progressBarId: progressBarFormValues?.progressBarId,
        points: progressBarFormValues?.progressBarPoints,
        progressBarOffers: progressBarOffers ?? []
      }),
    [progressBarFormValues, progressBarOffers]
  );

  return (
    <OfferPreviewShell containerClassName="flex justify-center">
      {({ generalTheme, storeScreen }) => {
        if (!offerDesignData) return null;

        const viewModel = generalTheme.bundlesViewModel as EBundlesViewModel;
        const internalModel =
          generalTheme.bundlesInternalViewModel ||
          EBundlesInternalViewModel.MAIN_SUB;

        return (
          <BundlesLayout bundlesViewModel={viewModel} isPreview>
            {renderingUtil.renderBundleByInternalModel(internalModel, {
              index: 0,
              ...(offerDesignData.backgroundImage && {
                backgroundSrc: offerDesignData.backgroundImage
              }),
              ...(offerDesignData.bundle?.backgroundColor && {
                backgroundColor: normalizeBgColor(
                  offerDesignData.bundle.backgroundColor
                )
              }),
              bundleBorderColor: storeScreen.bundleBorderColor,
              productList: productsList,
              bundleButtonColor: storeScreen.buttonColor,
              bundleButtonTextColor: storeScreen.buttonTextColor,
              fontFamily: generalTheme.font,
              prices: { priceDisplay, priceInUsdCents },
              inMobileMock: true,
              gameName: internalModel,
              salePercentage,
              salePercentageDisplayType,
              emblemBadges,
              ribbonBadges,
              progressBarPoints
            })}
          </BundlesLayout>
        );
      }}
    </OfferPreviewShell>
  );
};
